import { useEffect, useState } from 'react'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'
import { fetchTenantPortal } from '@/api/tenantApi'
import {
  clearTenantSlug,
  isNativeWhiteLabelBuild,
  isValidTenantSlugParam,
  normalizeTenantSlugParam,
  setTenantSlug,
} from '@/config/tenant'
import { isNativeAppRuntime } from '@/config/runtime'
import { useUser } from '@/contexts/UserContext'
import { useTenant } from '@/contexts/TenantContext'
import { TipoAcessoPage } from '@/pages/TipoAcessoPage'
import { isGuestSessionActive } from '@/utils/guestAuth'
import { loadAndApplyTheme } from '@/utils/theme'
import styles from './TenantEntryPage.module.css'

/**
 * Link da empresa (/e/:slug): grava o tenant na sessão e mostra a seleção de tipo de acesso.
 */
export function TenantEntryPage() {
  const { tenantSlug: rawSlug } = useParams<{ tenantSlug: string }>()
  const navigate = useNavigate()
  const { user } = useUser()
  const { refresh } = useTenant()
  const slug = normalizeTenantSlugParam(decodeURIComponent(rawSlug ?? ''))

  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState('')
  const [suspended, setSuspended] = useState(false)
  const [portalNome, setPortalNome] = useState('')
  const [ready, setReady] = useState(false)

  useEffect(() => {
    let cancelled = false

    async function load() {
      setLoading(true)
      setErro('')
      setSuspended(false)
      setReady(false)

      if (!slug || !isValidTenantSlugParam(slug)) {
        setErro('Link inválido. Peça à sua empresa o endereço completo da aplicação.')
        setLoading(false)
        return
      }

      try {
        const data = await fetchTenantPortal(slug)
        if (cancelled) return

        if (!data.ativo) {
          setSuspended(true)
          setPortalNome(data.nome)
          setLoading(false)
          return
        }

        setTenantSlug(data.slug)
        await refresh()
        await loadAndApplyTheme()
        if (!cancelled) {
          document.title = data.nome
          setReady(true)
        }
      } catch (e) {
        if (!cancelled) {
          setErro(e instanceof Error ? e.message : 'Não foi possível abrir esta empresa.')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [slug, refresh])

  if (ready) {
    if (user || isGuestSessionActive()) return <Navigate to="/menu" replace />
    return <TipoAcessoPage />
  }

  if (loading) {
    return (
      <main className={`standalone-page ${styles.page}`}>
        <p className={styles.loading}>A carregar…</p>
      </main>
    )
  }

  if (erro) {
    const native = isNativeWhiteLabelBuild() || isNativeAppRuntime()
    return (
      <main className={`standalone-page ${styles.page}`}>
        <div className={styles.card}>
          <h1 className={styles.title}>{native ? 'Não foi possível abrir' : 'Link não reconhecido'}</h1>
          <p className={styles.erro} role="alert">
            {erro}
          </p>
          {native ? (
            <button
              type="button"
              className={styles.linkBack}
              onClick={() => {
                setLoading(true)
                setErro('')
                window.location.reload()
              }}
            >
              Tentar novamente
            </button>
          ) : (
            <Link to="/" className={styles.linkBack}>
              ← Voltar
            </Link>
          )}
        </div>
      </main>
    )
  }

  if (suspended) {
    return (
      <main className={`standalone-page ${styles.page}`}>
        <div className={styles.card}>
          <h1 className={styles.title}>{portalNome || slug}</h1>
          <p className={styles.erro} role="alert">
            Esta conta está temporariamente indisponível. Contacte o suporte da plataforma.
          </p>
          <button
            type="button"
            className={styles.linkBack}
            onClick={() => {
              clearTenantSlug()
              navigate('/', { replace: true })
            }}
          >
            ← Voltar
          </button>
        </div>
      </main>
    )
  }

  return null
}
